import {
  Checkout,
  DirectCheckoutAddress,
  DirectCheckoutGiftCard,
  DirectCheckoutItems,
  DirectCheckoutProxyPickup,
  DirectCheckoutShipMode,
} from '@shared/data-access/interfaces';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { CustomHttpHeaders } from '@shared/data-access/enums';
import { Observable } from 'rxjs';
import { WINDOW } from '../window/window.service';

@Injectable({
  providedIn: 'root',
})
export class CheckoutDirectDao {
  private readonly http: HttpClient = inject(HttpClient);
  private readonly window = inject<Window>(WINDOW);
  private readonly baseUrl = '/api/v1/checkouts/direct';

  private readonly defaultHeaders = new HttpHeaders({
    Accept: 'application/json',
    'Content-Type': 'application/json',
    'Cache-Control': 'no-cache,no-store,must-revalidate,max-age=0',
    Pragma: 'no-cache',
    Referer: this.window?.location?.hostname ?? '',
    // Opts the response into ModernCheckoutInterceptor's modern -> legacy Checkout translation.
    [CustomHttpHeaders.X_CHK_MOD]: 'true',
    [CustomHttpHeaders.CHECKOUT]: 'direct',
  });

  // Add authorization header to interceptor
  createCheckout(checkoutItems: DirectCheckoutItems): Observable<Checkout> {
    return this.http.post<Checkout>(this.baseUrl, checkoutItems, { headers: this.defaultHeaders });
  }

  saveAddress(
    orderId: string,
    address: DirectCheckoutAddress,
    type: 'SHIPPING' | 'BILLING' | 'SHIPPINGBILLING',
  ): Observable<Checkout> {
    const httpOptions = {
      headers: this.defaultHeaders.set('x-api-version', '6'),
      withCredentials: true,
    };

    return this.http.post<Checkout>(
      `${this.baseUrl}/${orderId}/address?type=${type}`,
      address,
      httpOptions,
    );
  }

  addProxyPickup(
    orderId: string,
    proxyPickupInfo: DirectCheckoutProxyPickup,
  ): Observable<Checkout> {
    const httpOptions = {
      headers: this.defaultHeaders,
      withCredentials: true,
    };
    return this.http.put<Checkout>(
      `${this.baseUrl}/${orderId}/proxy-pickup`,
      proxyPickupInfo,
      httpOptions,
    );
  }

  deleteProxyPickup(orderId: string): Observable<Checkout> {
    const httpOptions = {
      headers: this.defaultHeaders,
      withCredentials: true,
    };
    return this.http.delete<Checkout>(`${this.baseUrl}/${orderId}/proxy-pickup`, httpOptions);
  }

  applyPromotion(orderId: string, promotionCode: string): Observable<Checkout> {
    const httpOptions = {
      headers: this.defaultHeaders.set('x-api-version', '6'),
      withCredentials: true,
    };
    return this.http.put<Checkout>(
      `${this.baseUrl}/${orderId}/promotion/cart`,
      { promotionCode },
      httpOptions,
    );
  }

  deletePromotion(orderId: string, promotionCode: string): Observable<Checkout> {
    const httpOptions = {
      headers: this.defaultHeaders.set('x-api-version', '6'),
      withCredentials: true,
      body: { promotionCode },
    };

    return this.http.delete<Checkout>(`${this.baseUrl}/${orderId}/promotion/cart`, httpOptions);
  }

  applyGiftCard(orderId: string, giftCardInfo: DirectCheckoutGiftCard): Observable<Checkout> {
    const httpOptions = {
      headers: this.defaultHeaders.set('x-api-version', '6'),
      withCredentials: true,
    };

    return this.http.post<Checkout>(
      `${this.baseUrl}/${orderId}/gift-cards`,
      giftCardInfo,
      httpOptions,
    );
  }

  deleteGiftCard(orderId: string, giftCardIndex: string): Observable<Checkout> {
    const httpOptions = {
      headers: this.defaultHeaders.set('x-api-version', '6'),
      withCredentials: true,
    };
    return this.http.delete<Checkout>(
      `${this.baseUrl}/${orderId}/gift-cards/${giftCardIndex}`,
      httpOptions,
    );
  }

  refreshCheckout(orderId: string): Observable<Checkout> {
    const httpOptions = {
      headers: this.defaultHeaders.set('x-api-version', '6'),
      withCredentials: true,
    };
    return this.http.patch<Checkout>(`${this.baseUrl}/${orderId}`, null, httpOptions);
  }

  updateShippingMode(
    orderId: string,
    groupNumber: number,
    shipMode: DirectCheckoutShipMode,
  ): Observable<Checkout> {
    const httpOptions = {
      headers: this.defaultHeaders.set('x-api-version', '6'),
      withCredentials: true,
    };
    return this.http.put<Checkout>(
      `${this.baseUrl}/${orderId}/shipping/selected/groups/${groupNumber}/mode`,
      shipMode,
      httpOptions,
    );
  }

  getSubmittedCheckout(orderId: string): Observable<Checkout> {
    const httpOptions = {
      headers: this.defaultHeaders.set('x-api-version', '6'),
      withCredentials: true,
    };
    return this.http.get<Checkout>(`${this.baseUrl}/submitted/${orderId}`, httpOptions);
  }

  submitCheckout(orderId: string): Observable<Checkout> {
    const httpOptions = {
      headers: this.defaultHeaders.set('x-api-version', '6'),
      withCredentials: true,
    };
    return this.http.post<Checkout>(`${this.baseUrl}/${orderId}`, null, httpOptions);
  }
}
